import { useEffect, useState, useMemo, Fragment } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  AlertTriangle, Clock3, Target, ClipboardX, CalendarRange, Sparkles,
  ChevronRight, Calendar, LayoutGrid, Wrench, Search, ChevronDown, ChevronUp,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

function fmtNum(n)  { return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }); }
function fmtNum1(n) { return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }); }
function fmtPct(n)  { return n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`; }
function fmtCurrency(n) {
  return Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "2-digit" }); }
  catch { return iso; }
}
function isoDayOnly(iso) {
  if (!iso) return "";
  return String(iso).slice(0, 10);
}

const RISK_BADGE = {
  "OVER BUDGET":    "bg-red-500/10 border-red-500/40 text-red-400",
  "AT RISK":        "bg-amber-500/10 border-amber-500/40 text-amber-400",
  "WITHIN BUDGET":  "bg-emerald-500/10 border-emerald-500/40 text-emerald-400",
  "UNDER TARGET":   "bg-amber-500/10 border-amber-500/40 text-amber-400",
  "ON TARGET":      "bg-emerald-500/10 border-emerald-500/40 text-emerald-400",
  "OVER TARGET":    "bg-cyan-500/10 border-cyan-500/40 text-cyan-400",
  "UNKNOWN - MISSING BUDGET": "bg-slate-500/10 border-slate-500/40 text-slate-300 border-dashed",
  "NO ACTIVITY":    "bg-white/5 border-white/15 text-muted-foreground",
  "MET":            "bg-emerald-500/10 border-emerald-500/40 text-emerald-400",
  "SHORT":          "bg-red-500/10 border-red-500/40 text-red-400",
  "NO OUTPUT YET":  "bg-amber-500/10 border-amber-500/40 text-amber-400",
};

function Badge({ label }) {
  if (!label) return <span className="text-muted-foreground">—</span>;
  const cls = RISK_BADGE[label.toUpperCase()] || "bg-white/5 border-white/20 text-muted-foreground";
  return (
    <span className={`inline-block text-[9px] tracking-widest uppercase px-2 py-0.5 border rounded-sm ${cls}`}>
      {label}
    </span>
  );
}

export default function LookAhead() {
  const { user } = useAuth();
  const isAdmin = user?.role_name === "Administrator" || user?.role === "admin";
  const plantScope = String(user?.plant_scope || "BOTH").toUpperCase();
  const canSeeSLC = isAdmin || plantScope === "SLC" || plantScope === "BOTH";
  const canSeeMex = isAdmin || plantScope === "MEX" || plantScope === "BOTH";
  const [showMexicoState, setShowMexico] = useState(() => !canSeeSLC && canSeeMex);
  const showMexico = !canSeeMex ? false : (!canSeeSLC ? true : showMexicoState);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/look-ahead", { params: { show_mexico: showMexico } })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [showMexico]);

  const tiles = data?.tiles || {};

  return (
    <div className="space-y-5 pb-10" data-testid="look-ahead-page">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#22D3EE]">09 · Look Ahead</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight mt-2 flex items-center gap-3">
            <CalendarRange className="text-[#22D3EE]" /> Look Ahead
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Forward-plan health across plants and phases · {showMexico ? "MEXICO" : "SLC"} plant scope
            {data?.as_of_week ? ` · Week of ${fmtDate(data.as_of_week)}` : ""}
          </p>
        </div>
        {canSeeSLC && canSeeMex ? (
          <button
            type="button" data-testid="look-ahead-toggle-mexico"
            onClick={() => setShowMexico((v) => !v)}
            className={`px-3 py-2 text-[10px] tracking-widest uppercase font-mono border transition-colors ${
              showMexico ? "bg-[#FBBF24] text-[#0A0B10] border-[#FBBF24]" : "bg-[#12141D] text-muted-foreground border-border hover:text-white"
            }`}>
            {showMexico ? "Showing Mexico" : "Show Mexico Plants"}
          </button>
        ) : (
          <span className="px-3 py-2 text-[10px] tracking-widest uppercase font-mono border border-border bg-[#0A0B10] text-muted-foreground"
                data-testid="look-ahead-plant-scope-locked">
            Plant: {canSeeMex ? "Mexico" : "SLC"} only
          </span>
        )}
      </header>

      {/* Top tiles (4 across) */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="look-ahead-tiles">
        <Tile
          testId="tile-over-budget"
          label="Departments Over Budget"
          value={fmtNum(tiles.over_budget_depts)}
          sub={`of ${fmtNum(tiles.total_depts)} in view`}
          accent="#EF4444"
          icon={<AlertTriangle size={14} />}
        />
        <Tile
          testId="tile-overdue"
          label="Overdue Pieces"
          value={fmtNum(tiles.total_overdue)}
          sub={`max ${fmtNum(tiles.max_days_overdue)} days`}
          accent="#F59E0B"
          icon={<Clock3 size={14} />}
        />
        <Tile
          testId="tile-coverage"
          label="Avg Weekly Coverage"
          value={fmtPct(tiles.avg_coverage_pct)}
          sub="of 1,000 SQFT target"
          accent={tiles.avg_coverage_pct >= 100 ? "#34D399" : tiles.avg_coverage_pct >= 75 ? "#FBBF24" : "#EF4444"}
          icon={<Target size={14} />}
        />
        <Tile
          testId="tile-forecast-issues"
          label="Forecast Issues"
          value={fmtNum(tiles.forecast_issues)}
          sub="rows missing budget records"
          accent="#A78BFA"
          icon={<ClipboardX size={14} />}
        />
      </section>

      {/* Budget Risk Worklist */}
      <Section title="Budget Risk Worklist" subtitle="One row per Plant / Phase / Category — sorted by hours over budget" testId="section-worklist">
        <div className="overflow-x-auto max-h-[520px]">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8] sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-3">Plant</th>
                <th className="py-2.5 px-3">Phase</th>
                <th className="py-2.5 px-3">Category</th>
                <th className="py-2.5 px-3 text-right">Jobs Scheduled</th>
                <th className="py-2.5 px-3 text-right">Jobs Over Budget</th>
                <th className="py-2.5 px-3 text-right">Hrs Over Budget</th>
                <th className="py-2.5 px-3 text-right">Overdue Pcs</th>
                <th className="py-2.5 px-3 text-right">Coverage</th>
                <th className="py-2.5 px-3">Dept Risk</th>
                <th className="py-2.5 px-3">Target</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="py-8 text-center uppercase tracking-widest text-muted-foreground">Loading…</td></tr>}
              {!loading && (data?.worklist || []).length === 0 && (
                <tr><td colSpan={10} className="py-8 text-center uppercase tracking-widest text-muted-foreground">No departments in view</td></tr>
              )}
              {(data?.worklist || []).map((r, i) => (
                <tr key={i} className="border-t border-border/40 hover:bg-[#1A1C28]/40" data-testid={`worklist-row-${i}`}>
                  <td className="py-2 px-3 font-mono text-white/90">{r.plant || "—"}</td>
                  <td className="py-2 px-3 font-mono text-muted-foreground">{r.phase || "—"}</td>
                  <td className="py-2 px-3 text-muted-foreground max-w-[220px] truncate" title={r.category}>{r.category || "—"}</td>
                  <td className="py-2 px-3 font-mono text-right tabular-nums">{fmtNum(r.jobs_scheduled)}</td>
                  <td className={`py-2 px-3 font-mono text-right tabular-nums font-semibold ${r.jobs_over_budget > 0 ? "text-red-400" : ""}`}>{fmtNum(r.jobs_over_budget)}</td>
                  <td className={`py-2 px-3 font-mono text-right tabular-nums font-semibold ${r.total_hours_over_budget > 0 ? "text-red-400" : "text-emerald-300"}`}>
                    {r.total_hours_over_budget > 0 ? "+" : ""}{fmtNum1(r.total_hours_over_budget)}
                  </td>
                  <td className={`py-2 px-3 font-mono text-right tabular-nums ${r.total_overdue_pieces > 0 ? "text-amber-300" : ""}`}>{fmtNum(r.total_overdue_pieces)}</td>
                  <td className="py-2 px-3 font-mono text-right tabular-nums">{fmtPct(r.weekly_target_coverage_pct)}</td>
                  <td className="py-2 px-3"><Badge label={r.department_risk} /></td>
                  <td className="py-2 px-3"><Badge label={r.weekly_target_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Overdue Backlog */}
      <Section title="Overdue Backlog" subtitle="Jobs whose scheduled pieces have slipped past their target date" testId="section-backlog">
        <div className="overflow-x-auto max-h-[420px]">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8] sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-3">Job</th>
                <th className="py-2.5 px-3">Plant</th>
                <th className="py-2.5 px-3">Phase</th>
                <th className="py-2.5 px-3">Category</th>
                <th className="py-2.5 px-3 text-right">Overdue Pcs</th>
                <th className="py-2.5 px-3 text-right">Max Days Overdue</th>
                <th className="py-2.5 px-3 text-right">Scheduled Pcs</th>
              </tr>
            </thead>
            <tbody>
              {!loading && (data?.backlog || []).length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center uppercase tracking-widest text-emerald-400">
                  🎉  No overdue pieces in view
                </td></tr>
              )}
              {(data?.backlog || []).map((r, i) => (
                <tr key={i} className="border-t border-border/40 hover:bg-[#1A1C28]/40" data-testid={`backlog-row-${i}`}>
                  <td className="py-2 px-3 font-mono text-[#00D4FF]">{r.job || "—"}</td>
                  <td className="py-2 px-3 font-mono text-white/90">{r.plant || "—"}</td>
                  <td className="py-2 px-3 font-mono text-muted-foreground">{r.phase || "—"}</td>
                  <td className="py-2 px-3 text-muted-foreground">{r.category || "—"}</td>
                  <td className="py-2 px-3 font-mono text-right tabular-nums text-amber-300 font-semibold">{fmtNum(r.overdue_pieces)}</td>
                  <td className={`py-2 px-3 font-mono text-right tabular-nums font-semibold ${
                    r.max_days_overdue > 30 ? "text-red-400" : r.max_days_overdue > 14 ? "text-amber-400" : "text-white/80"
                  }`}>{fmtNum(r.max_days_overdue)}</td>
                  <td className="py-2 px-3 font-mono text-right tabular-nums text-muted-foreground">{fmtNum(r.scheduled_pieces)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Data-hygiene callouts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CalloutPanel
          testId="callout-missing-budget"
          title="Missing Budget Records"
          subtitle="Data-hygiene flag — jobs without a matching Plant/Phase budget row"
          icon={ClipboardX}
          accent="#F59E0B"
          rows={data?.missing_budget || []}
          renderRow={(r) => (
            <>
              <span className="font-mono text-[#00D4FF]">{r.job}</span>
              <span className="text-muted-foreground text-xs">{r.plant} · {r.phase} · {r.category}</span>
              <span className="text-[10px] font-mono text-amber-300 uppercase tracking-widest">{r.forecast_status}</span>
            </>
          )}
          empty="All jobs have matching budget records"
        />
        <CalloutPanel
          testId="callout-new-jobs"
          title="New Jobs (Budget-Basis)"
          subtitle="No actual history yet — numbers use budgeted rates until first data lands"
          icon={Sparkles}
          accent="#A78BFA"
          rows={data?.new_jobs || []}
          renderRow={(r) => (
            <>
              <span className="font-mono text-[#00D4FF]">{r.job}</span>
              <span className="text-muted-foreground text-xs">{r.plant} · {r.phase} · {r.category}</span>
              <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">{fmtNum1(r.scheduled_sqft)} sf</span>
            </>
          )}
          empty="No new jobs in view"
        />
      </div>

      {/* Card 1 — Scheduled Workload */}
      <ScheduledWorkloadCard showMexico={showMexico} />

      {/* Card 2 — Job Schedule vs Budget */}
      <JobScheduleVsBudgetCard showMexico={showMexico} />
    </div>
  );
}

function Tile({ label, value, sub, accent, icon, testId }) {
  return (
    <div className="relative bg-[#12141D] border border-border p-5" data-testid={testId}>
      <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: accent }} />
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{label}</span>
        <div className="h-7 w-7 border border-border bg-[#0A0B10] flex items-center justify-center" style={{ color: accent }}>{icon}</div>
      </div>
      <div className="font-heading font-bold text-3xl tabular-nums mt-3" style={{ color: accent }}>{value}</div>
      <div className="text-[11px] text-muted-foreground font-mono mt-1">{sub}</div>
    </div>
  );
}

function Section({ title, subtitle, testId, children }) {
  return (
    <section className="bg-[#12141D] border border-border" data-testid={testId}>
      <header className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-bold text-base">{title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
      </header>
      {children}
    </section>
  );
}

function CalloutPanel({ title, subtitle, icon: Icon, accent, rows, renderRow, empty, testId }) {
  return (
    <section className="bg-[#12141D] border border-border overflow-hidden" data-testid={testId}>
      <span className="block h-[2px] w-full" style={{ background: accent }} />
      <header className="px-5 py-3.5 flex items-center gap-2 border-b border-border">
        {Icon && <Icon size={16} style={{ color: accent }} />}
        <div className="flex-1">
          <h3 className="font-heading font-bold text-sm">{title}</h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-muted-foreground">{rows.length}</span>
      </header>
      <div className="max-h-[260px] overflow-y-auto">
        {rows.length === 0 ? (
          <div className="py-8 text-center uppercase tracking-widest text-emerald-400 text-xs">✔ {empty}</div>
        ) : (
          <ul className="divide-y divide-border/40">
            {rows.map((r, i) => (
              <li key={i} className="px-5 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm hover:bg-[#1A1C28]/40" data-testid={`${testId}-row-${i}`}>
                <ChevronRight size={12} className="text-muted-foreground" />
                {renderRow(r)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Card 1 — Scheduled Workload (Plant × Phase × Category)                    */
/* -------------------------------------------------------------------------- */
function ScheduledWorkloadCard({ showMexico }) {
  const [mode, setMode] = useState("daily");     // "daily" | "weekly"
  const [selected, setSelected] = useState("");  // full ISO string
  const [plant, setPlant] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { mode, show_mexico: showMexico };
    if (selected) params.date = isoDayOnly(selected);
    if (plant)    params.plant = plant;
    api.get("/look-ahead/scheduled-workload", { params })
      .then((r) => { if (!cancelled) { setData(r.data); if (!selected && r.data?.selected) setSelected(r.data.selected); } })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selected, plant, showMexico]);

  // Reset date/plant when switching plant scope so we don't hold a stale value
  useEffect(() => { setSelected(""); setPlant(""); }, [showMexico, mode]);

  const options = mode === "daily" ? (data?.available_dates || []) : (data?.available_weeks || []);
  const plants = data?.plants || [];
  const totals = data?.totals || {};
  const rows = data?.breakdown || [];

  return (
    <section className="bg-[#12141D] border border-border" data-testid="section-scheduled-workload">
      <header className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <LayoutGrid size={16} className="text-[#22D3EE]" />
          <div>
            <h3 className="font-heading font-bold text-base">Scheduled Workload</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Plant × Phase × Category — <span className="text-[#22D3EE]">forecast/needed</span> vs SQFT produced
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* mode toggle */}
          <div className="inline-flex border border-border overflow-hidden" data-testid="workload-mode-toggle">
            {["daily", "weekly"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                data-testid={`workload-mode-${m}`}
                className={`px-3 py-1.5 text-[10px] tracking-widest uppercase font-mono transition-colors ${
                  mode === m ? "bg-[#22D3EE] text-[#0A0B10]" : "bg-[#0A0B10] text-muted-foreground hover:text-white"
                }`}>
                {m}
              </button>
            ))}
          </div>
          {/* date/week picker */}
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            data-testid="workload-date-select"
            className="bg-[#0A0B10] border border-border text-xs font-mono px-2.5 py-1.5 uppercase tracking-widest text-white/90">
            {options.length === 0 && <option value="">—</option>}
            {options.map((d) => (
              <option key={d} value={d}>
                {mode === "daily" ? fmtDate(d) : `Week of ${fmtDate(d)}`}
              </option>
            ))}
          </select>
          {/* plant filter */}
          <select
            value={plant}
            onChange={(e) => setPlant(e.target.value)}
            data-testid="workload-plant-select"
            className="bg-[#0A0B10] border border-border text-xs font-mono px-2.5 py-1.5 uppercase tracking-widest text-white/90">
            <option value="">All Plants</option>
            {plants.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </header>

      {/* Totals — three stat tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border" data-testid="workload-totals">
        <StatCell
          testId="workload-total-sqft"
          label={mode === "daily" ? "SQFT Scheduled (day)" : "SQFT Scheduled (week)"}
          value={fmtNum1(totals.scheduled_sqft)}
          suffix="sf"
          accent="#22D3EE"
        />
        <StatCell
          testId="workload-total-hours"
          label="Labor Hours Needed"
          value={fmtNum1(totals.projected_labor_hours)}
          suffix="hrs"
          accent="#A78BFA"
        />
        <StatCell
          testId="workload-total-material"
          label="Material Cost Needed"
          value={fmtCurrency(totals.projected_material_cost)}
          suffix=""
          accent="#FBBF24"
        />
      </div>

      {/* Breakdown table */}
      <div className="overflow-x-auto max-h-[520px]">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8] sticky top-0 z-10">
            <tr>
              <th className="py-2.5 px-3">Plant</th>
              <th className="py-2.5 px-3">Phase</th>
              <th className="py-2.5 px-3">Category</th>
              <th className="py-2.5 px-3 text-right">SQFT Scheduled</th>
              <th className="py-2.5 px-3 text-right">Labor Hrs Needed</th>
              <th className="py-2.5 px-3 text-right">Material $ Needed</th>
              <th className="py-2.5 px-3 text-right">Target SF</th>
              <th className="py-2.5 px-3 text-right">Coverage</th>
              <th className="py-2.5 px-3">Target Status</th>
              <th className="py-2.5 px-3 text-right">SQFT Produced</th>
              <th className="py-2.5 px-3">Target vs Produced</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="py-8 text-center uppercase tracking-widest text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="py-8 text-center uppercase tracking-widest text-muted-foreground">No scheduled workload in view</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border/40 hover:bg-[#1A1C28]/40" data-testid={`workload-row-${i}`}>
                <td className="py-2 px-3 font-mono text-white/90">{r.plant || "—"}</td>
                <td className="py-2 px-3 font-mono text-muted-foreground">{r.phase || "—"}</td>
                <td className="py-2 px-3 text-muted-foreground max-w-[220px] truncate" title={r.category}>{r.category || "—"}</td>
                <td className="py-2 px-3 font-mono text-right tabular-nums text-[#22D3EE] font-semibold">{fmtNum1(r.scheduled_sqft)}</td>
                <td className="py-2 px-3 font-mono text-right tabular-nums text-[#A78BFA]">{fmtNum1(r.projected_labor_hours)}</td>
                <td className="py-2 px-3 font-mono text-right tabular-nums text-[#FBBF24]">{fmtCurrency(r.projected_material_cost)}</td>
                <td className="py-2 px-3 font-mono text-right tabular-nums text-muted-foreground" data-testid={`workload-row-${i}-target`}
                  title={r.target_shared ? `Shared Mold target — ${fmtNum1(r.group_scheduled_sqft)} SF scheduled across all Mold categories` : undefined}>
                  {fmtNum1(r.target_sqft)}{r.target_shared && <span className="ml-1 text-[9px] uppercase tracking-widest text-[#A78BFA]">shared</span>}
                </td>
                <td className="py-2 px-3 font-mono text-right tabular-nums">{fmtPct(r.target_coverage_pct)}</td>
                <td className="py-2 px-3"><Badge label={r.target_status} /></td>
                <td className="py-2 px-3 font-mono text-right tabular-nums text-emerald-300 font-semibold" data-testid={`workload-row-${i}-produced`} title={r.produced_source}>
                  {fmtNum1(r.produced_sqft)}
                </td>
                <td className="py-2 px-3" data-testid={`workload-row-${i}-produced-vs-target`}>
                  {r.produced_vs_target_pct == null ? <span className="text-muted-foreground">—</span> : (
                    <span className="inline-flex items-center gap-2 font-mono tabular-nums">
                      {fmtPct(r.produced_vs_target_pct)}
                      <Badge label={r.produced_status} />
                      {r.produced_status === "SHORT" && <span className="text-[10px] text-red-300">{fmtNum1(Math.abs(r.produced_gap_sqft))} SF</span>}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StatCell({ label, value, suffix, accent, testId }) {
  return (
    <div className="bg-[#12141D] p-4" data-testid={testId}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-heading font-bold text-2xl tabular-nums" style={{ color: accent }}>{value}</span>
        {suffix && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Card 2 — Job Schedule vs Budget                                           */
/* -------------------------------------------------------------------------- */
function JobScheduleVsBudgetCard({ showMexico }) {
  const [mode, setMode] = useState("daily");
  const [selected, setSelected] = useState("");
  const [plant, setPlant] = useState("");
  const [risk, setRisk] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);   // row key

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { mode, show_mexico: showMexico };
    if (selected) params.date = isoDayOnly(selected);
    if (plant)    params.plant = plant;
    if (risk)     params.risk = risk;
    if (search)   params.search = search;
    api.get("/look-ahead/job-schedule-vs-budget", { params })
      .then((r) => { if (!cancelled) { setData(r.data); if (!selected && r.data?.selected) setSelected(r.data.selected); } })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selected, plant, risk, search, showMexico]);

  useEffect(() => {
    setSelected(""); setPlant(""); setRisk(""); setSearch(""); setExpanded(null);
  }, [showMexico, mode]);

  const options = data?.available_dates || [];
  const plants  = data?.plants || [];
  const riskCounts = data?.risk_counts || {};
  const jobs = data?.jobs || [];

  return (
    <section className="bg-[#12141D] border border-border" data-testid="section-job-schedule-vs-budget">
      <header className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Wrench size={16} className="text-[#EF4444]" />
          <div>
            <h3 className="font-heading font-bold text-base">Job Schedule vs Budget</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              One row per Job × Plant × Phase × Category — over-budget jobs listed first
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border border-border overflow-hidden" data-testid="jsvb-mode-toggle">
            {["daily", "weekly"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                data-testid={`jsvb-mode-${m}`}
                className={`px-3 py-1.5 text-[10px] tracking-widest uppercase font-mono transition-colors ${
                  mode === m ? "bg-[#EF4444] text-[#0A0B10]" : "bg-[#0A0B10] text-muted-foreground hover:text-white"
                }`}>
                {m}
              </button>
            ))}
          </div>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            data-testid="jsvb-date-select"
            className="bg-[#0A0B10] border border-border text-xs font-mono px-2.5 py-1.5 uppercase tracking-widest text-white/90">
            {options.length === 0 && <option value="">—</option>}
            {options.map((d) => (
              <option key={d} value={d}>
                {mode === "daily" ? fmtDate(d) : `Week of ${fmtDate(d)}`}
              </option>
            ))}
          </select>
          <select
            value={plant}
            onChange={(e) => setPlant(e.target.value)}
            data-testid="jsvb-plant-select"
            className="bg-[#0A0B10] border border-border text-xs font-mono px-2.5 py-1.5 uppercase tracking-widest text-white/90">
            <option value="">All Plants</option>
            {plants.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            value={risk}
            onChange={(e) => setRisk(e.target.value)}
            data-testid="jsvb-risk-select"
            className="bg-[#0A0B10] border border-border text-xs font-mono px-2.5 py-1.5 uppercase tracking-widest text-white/90">
            <option value="">All Risk</option>
            {["OVER BUDGET", "WITHIN BUDGET", "UNKNOWN - MISSING BUDGET"].map((k) => (
              <option key={k} value={k}>
                {k} {riskCounts[k] ? `(${riskCounts[k]})` : ""}
              </option>
            ))}
          </select>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search job…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="jsvb-job-search"
              className="bg-[#0A0B10] border border-border text-xs font-mono pl-7 pr-2.5 py-1.5 uppercase tracking-widest text-white/90 w-40 placeholder:text-muted-foreground/50 placeholder:normal-case placeholder:tracking-normal"
            />
          </div>
        </div>
      </header>

      <div className="overflow-x-auto max-h-[640px]">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8] sticky top-0 z-10">
            <tr>
              <th className="py-2.5 px-3 w-4"></th>
              <th className="py-2.5 px-3">Job</th>
              <th className="py-2.5 px-3">Plant</th>
              <th className="py-2.5 px-3">Phase / Cat</th>
              <th className="py-2.5 px-3 text-right">Scheduled SF</th>
              <th className="py-2.5 px-3 text-right">Projected Hrs</th>
              <th className="py-2.5 px-3 text-right">Projected $ Mat</th>
              <th className="py-2.5 px-3 text-right">Hrs Remaining</th>
              <th className="py-2.5 px-3 text-right">Hrs Over</th>
              <th className="py-2.5 px-3">Risk</th>
              <th className="py-2.5 px-3">Actual vs Plan</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="py-8 text-center uppercase tracking-widest text-muted-foreground">Loading…</td></tr>}
            {!loading && jobs.length === 0 && (
              <tr><td colSpan={11} className="py-8 text-center uppercase tracking-widest text-muted-foreground">No jobs match current filters</td></tr>
            )}
            {jobs.map((r, i) => {
              const key = `${r.job}|${r.plant}|${r.phase}|${r.category}|${i}`;
              const isOpen = expanded === key;
              const hoursRem = r.hours_remaining_after_scheduled;
              const hoursOver = r.hours_over_budget;
              return (
                <Fragment key={key}>
                  <tr
                    className="border-t border-border/40 hover:bg-[#1A1C28]/40 cursor-pointer"
                    onClick={() => setExpanded(isOpen ? null : key)}
                    data-testid={`jsvb-row-${i}`}
                  >
                    <td className="py-2 px-3 text-muted-foreground">
                      {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </td>
                    <td className="py-2 px-3 font-mono text-[#00D4FF]">{r.job}</td>
                    <td className="py-2 px-3 font-mono text-white/90">{r.plant}</td>
                    <td className="py-2 px-3 font-mono text-muted-foreground max-w-[200px] truncate" title={`${r.phase} · ${r.category}`}>
                      {(r.phase || "—")}{r.category ? ` · ${r.category}` : ""}
                    </td>
                    <td className="py-2 px-3 font-mono text-right tabular-nums text-[#22D3EE]">{fmtNum1(r.scheduled_sqft)}</td>
                    <td className="py-2 px-3 font-mono text-right tabular-nums text-[#A78BFA]">{fmtNum1(r.projected_labor_hours)}</td>
                    <td className="py-2 px-3 font-mono text-right tabular-nums text-[#FBBF24]">{fmtCurrency(r.projected_material_cost)}</td>
                    <td className={`py-2 px-3 font-mono text-right tabular-nums font-semibold ${
                      hoursRem === null || hoursRem === undefined ? "text-muted-foreground" :
                      hoursRem < 0 ? "text-red-400" : "text-emerald-300"
                    }`}>
                      {hoursRem === null || hoursRem === undefined ? "—" : `${hoursRem > 0 ? "+" : ""}${fmtNum1(hoursRem)}`}
                    </td>
                    <td className={`py-2 px-3 font-mono text-right tabular-nums ${hoursOver > 0 ? "text-red-400 font-semibold" : "text-muted-foreground"}`}>
                      {hoursOver === null || hoursOver === undefined ? "—" : fmtNum1(hoursOver)}
                    </td>
                    <td className="py-2 px-3"><Badge label={r.schedule_risk} /></td>
                    <td className="py-2 px-3" data-testid={`jsvb-row-${i}-verdict`}><Badge label={r.period_verdict} /></td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-[#0A0B10]" data-testid={`jsvb-row-${i}-detail`}>
                      <td colSpan={11} className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                          <DetailBlock title="Budget" accent="#94A3B8" items={[
                            ["Estimated SQFT",         fmtNum1(r.estimated_sqft)],
                            ["Budget Hours",           fmtNum1(r.budget_hours)],
                            ["Budget $/SF Material",   r.budget_cost_per_material === null ? "—" : `$${fmtNum1(r.budget_cost_per_material)}`],
                            ["Budget Productivity",    r.budget_productivity_sqft_per_hour === null ? "—" : `${Number(r.budget_productivity_sqft_per_hour).toFixed(2)} sf/hr`],
                          ]} />
                          <DetailBlock title="Planned (period)" accent="#A78BFA" items={[
                            ["Scheduled SQFT",        fmtNum1(r.scheduled_sqft)],
                            ["Cumulative Sched SF",   fmtNum1(r.cumulative_scheduled_sqft)],
                            ["Projected Labor Hrs",   fmtNum1(r.projected_labor_hours)],
                            ["Projected Material $",  fmtCurrency(r.projected_material_cost)],
                          ]} />
                          <DetailBlock title="Actual (period)" accent="#22D3EE" testid={`jsvb-row-${i}-actual`} items={[
                            ["SQFT Produced",         fmtNum1(r.actual_period_sqft)],
                            ["Hours Worked",          fmtNum1(r.actual_period_hours)],
                            ["Material $ Used",       fmtCurrency(r.actual_period_material)],
                            ["Actual Productivity",   r.actual_sf_per_hour == null ? "—" : `${Number(r.actual_sf_per_hour).toFixed(2)} sf/hr`],
                          ]} />
                          <DetailBlock title="Job to date" accent="#64748B" items={[
                            ["Net SQFT",           fmtNum1(r.net_sqft)],
                            ["SQFT Completion",    fmtPct(r.sqft_completion_pct)],
                            ["Actual Hours",       fmtNum1(r.actual_hours)],
                            ["Actual Material $",  r.actual_material_cost == null ? "—" : fmtCurrency(r.actual_material_cost)],
                            ["Unused Budget Hrs",  r.unused_budget_hours === null ? "—" : `${r.unused_budget_hours > 0 ? "+" : ""}${fmtNum1(r.unused_budget_hours)}`],
                          ]} />
                        </div>
                        <VerdictLine r={r} testid={`jsvb-row-${i}-verdict-line`} />
                        <div className="mt-3 text-[11px] font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-4 flex-wrap">
                          <span>Forecast: <span className="text-white/80">{r.forecast_status || "—"}</span></span>
                          <span>Lifecycle: <span className="text-white/80">{r.job_lifecycle_status || "—"}</span></span>
                          <span>Basis: <span className="text-white/80">{r.forecast_basis || "—"}</span></span>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const VERDICT_TONE = {
  "WITHIN BUDGET": "border-emerald-500/40 text-emerald-300",
  "OVER BUDGET":   "border-red-500/40 text-red-300",
  "NO OUTPUT YET": "border-amber-500/40 text-amber-300",
};

function verdictText(r) {
  const sf = fmtNum1(r.actual_period_sqft);
  const used = fmtNum1(r.actual_period_hours);
  const allowed = r.earned_period_hours == null ? "—" : fmtNum1(r.earned_period_hours);
  const mat = r.allowed_period_material == null ? "" :
    ` Material ${fmtCurrency(r.actual_period_material)} used vs ${fmtCurrency(r.allowed_period_material)} allowed.`;
  switch (r.period_verdict) {
    case "WITHIN BUDGET": return `Worked within budget — ${sf} SF took ${used} hrs vs ${allowed} hrs allowed by budget productivity.${mat}`;
    case "OVER BUDGET":   return `Over budget — ${sf} SF took ${used} hrs vs ${allowed} hrs allowed by budget productivity.${mat}`;
    case "NO OUTPUT YET": return `${used} hrs and ${fmtCurrency(r.actual_period_material)} material spent in this period with no SF produced yet.`;
    case "NO ACTIVITY":   return "No SF, hours or material recorded in this period.";
    default:              return "Budget productivity missing — can't judge this period.";
  }
}

function periodLabel(r) {
  if (!r.period_start) return "";
  const f = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const end = new Date(`${r.period_end}T00:00:00`); end.setDate(end.getDate() - 1);
  const endIso = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`;
  return endIso === r.period_start ? f(r.period_start) : `${f(r.period_start)} – ${f(endIso)}`;
}

function VerdictLine({ r, testid }) {
  const tone = VERDICT_TONE[r.period_verdict] || "border-border text-muted-foreground";
  return (
    <div className={`mt-3 border-l-2 pl-3 py-1 text-xs ${tone}`} data-testid={testid}>
      <span className="font-mono uppercase tracking-widest text-[10px] mr-2">Planned vs Actual · {periodLabel(r)}:</span>
      {verdictText(r)}
    </div>
  );
}

function DetailBlock({ title, accent, items, testid }) {
  return (
    <div className="bg-[#12141D] border border-border p-3" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase pb-2 border-b border-border/60" style={{ color: accent }}>{title}</div>
      <dl className="mt-2 space-y-1.5 text-xs">
        {items.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-3">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="font-mono tabular-nums text-white/90">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

